import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import type { Money } from '@/types'

dayjs.extend(relativeTime)

export function formatDate(date?: string, fmt = 'DD MMM YYYY'): string {
  if (!date) return '—'
  return dayjs(date).format(fmt)
}

export function formatDateTime(date?: string): string {
  if (!date) return '—'
  return dayjs(date).format('DD MMM YYYY, hh:mm A')
}

export function formatTime(date?: string): string {
  if (!date) return '—'
  return dayjs(date).format('hh:mm A')
}

export function fromNow(date?: string): string {
  if (!date) return '—'
  return dayjs(date).fromNow()
}

/** Compact Indian-style currency formatting (Lakh / Crore aware). */
export function formatMoney(money?: Money): string {
  if (!money) return '—'
  const { amount, currency } = money
  if (currency === 'INR') {
    if (amount >= 1e7) return `₹${(amount / 1e7).toFixed(2)} Cr`
    if (amount >= 1e5) return `₹${(amount / 1e5).toFixed(2)} L`
    return `₹${amount.toLocaleString('en-IN')}`
  }
  if (amount >= 1e6) return `$${(amount / 1e6).toFixed(2)}M`
  if (amount >= 1e3) return `$${(amount / 1e3).toFixed(1)}K`
  return `$${amount.toLocaleString('en-US')}`
}

export function formatNumber(n: number): string {
  return n.toLocaleString('en-IN')
}

export function formatCompact(n: number): string {
  return Intl.NumberFormat('en-IN', { notation: 'compact', maximumFractionDigits: 1 }).format(n)
}

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`
}

export function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase())
    .join('')
}

export function fullName(first: string, last: string): string {
  return `${first} ${last}`.trim()
}

/** Deterministic pastel colour from a string (avatars, chart series). */
export function colorFromString(str: string): string {
  let hash = 0
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash)
  const hue = Math.abs(hash) % 360
  return `hsl(${hue} 65% 45%)`
}
